import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';

interface ButtonProps {
  primary?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  fullWidth?: boolean;
  small?: boolean;
  large?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
}

const StyledButton = styled.button<Omit<ButtonProps, 'children' | 'onClick' | 'type' | 'className'>>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => (props.small ? '0.875rem' : props.large ? '1.125rem' : '1rem')};
  padding: ${props => {
    if (props.small) return `${theme.spacing.xs} ${theme.spacing.md}`;
    if (props.large) return `${theme.spacing.md} ${theme.spacing.xl}`;
    return `${theme.spacing.sm} ${theme.spacing.lg}`;
  }};
  width: ${props => (props.fullWidth ? '100%' : 'auto')};
  background-color: ${props => {
    if (props.secondary) return theme.colors.secondary;
    if (props.tertiary) return theme.colors.tertiary;
    return theme.colors.primary;
  }};
  color: white;
  border: none;
  border-radius: 30px;
  font-weight: 600;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.6 : 1)};
  transition: all 0.3s ease-in-out;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  /* Mobile touch optimizations */
  min-height: ${theme.mobile.minTouchTarget};
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, 
      ${theme.colors.primary}, 
      ${theme.colors.tertiary}
    );
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
    
    &::after {
      opacity: 1;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
  }
  
  /* Mobile-specific styles */
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${props => {
      if (props.small) return `${theme.spacing.sm} ${theme.spacing.md}`;
      if (props.large) return `${theme.spacing.lg} ${theme.spacing.xl}`;
      return `${theme.spacing.md} ${theme.spacing.lg}`;
    }};
    font-size: ${props => (props.small ? '0.875rem' : props.large ? '1.125rem' : '1rem')};
    border-radius: 12px;
    min-width: ${props => props.small ? '80px' : '100px'};
    
    /* Disable hover effects on touch devices */
    &:hover:not(:disabled) {
      transform: none;
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
      
      &::after {
        opacity: 0;
      }
    }
    
    /* Enhanced active state for touch feedback */
    &:active:not(:disabled) {
      transform: scale(0.98);
      opacity: 0.8;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    }
  }
`;

const Button: React.FC<ButtonProps> = ({ 
  children, 
  onClick, 
  type = 'button',
  className,
  ...rest 
}) => {
  return (
    <StyledButton onClick={onClick} type={type} className={className} {...rest}>
      {children}
    </StyledButton>
  );
};

export default Button; 